import type {CarrierId} from '@myparcel-frontend/core-api-service';
import type {
  PostAddNoteData,
  PostAssignToUserData,
  PostUnassignFromUserData,
  PostUnpreparePackagesForShipmentData,
  PostRemoveNotesData,
  PostEditNoteData,
  Order,
  PackageResponse,
  PostAddPackagesData,
  PostCancelData,
  PostCreateFromShippablePackagesData,
  PostPreparePackagesForShipmentData,
  GetOrdersData,
  PostImportData,
  GetPicklistData,
  GetPackingSlipsData,
} from '@myparcel-frontend/order-service';
import type {ProblemDetailsInternalServer, ProblemDetailsClient} from '@myparcel-frontend/service-utils';
import {useNotification} from '@myparcel-frontend/notifications';
import {activeOrderStatuses, OrderStatus, useOrderService} from '@myparcel-frontend/order-service';
import {groupBulkResponsesBySuccess} from '@myparcel-frontend/service-utils';
import {ShipmentLabel, SubscriptionFeature} from '@myparcel-frontend/types';
import {dayjs, file} from '@myparcel-frontend/utils';
import {storeToRefs} from 'pinia';
import {computed, onUnmounted} from 'vue';
import {useOrderFiltersStore} from '../../store/orderFiltersStore/orderFiltersStore';
import {useOrderStore} from '../../store/orderStore/orderStore';
import {TemporaryOrderNoteIdPrefix} from '../../types/order';
import {removeEmpty} from '../../utils/removeEmpty';
import useAccount from '../useAccount/useAccount';
import useAcl from '../useAcl';
import {useDirectPrinting} from '../useDirectPrinting/useDirectPrinting';
import {usePrintOrDownloadMode} from '../usePrintOrDownloadMode/usePrintOrDownloadMode';
import {useShipmentLabel} from '../useShipmentLabel/useShipmentLabel';
import useSubscription from '../useSubscription';
import useTableRowHighlights from '../useTableRowHighlights';
import useUiSettings from '../useUiSettings';

// TODO: In the future, the shipment_label setting will be sourced from IAM features instead of account settings.
// TODO: The 'nested' (COMBINED) layout is not yet supported by the packing slips API. It will require separate API calls and will be implemented later.
const shipmentLabelLayoutMap: Record<string, GetPackingSlipsData['query']['shipmentLabelsLayout']> = {
  [ShipmentLabel.Apart]: 'INTERLEAVED',
};

export const useOrders = () => {
  let orderAbortController: AbortController;
  let orderByIdAbortController: AbortController;

  const orderStore = useOrderStore();
  const orderFiltersStore = useOrderFiltersStore();
  const {hasPermission, hasSubscriptionFeature} = useAcl();
  const {addNotification} = useNotification();
  const {uiSettings} = useUiSettings();
  const {activeBundlesSubscription} = useSubscription();
  const {accountSettings} = useAccount();
  const {isShipmentDownloadable} = useShipmentLabel();
  const {packingTableIsActivated, pendingPrintJobs} = useDirectPrinting();
  const {isPrintMode} = usePrintOrDownloadMode();

  const {orderList, orderListAmount, statusesWithNewOrders, isBulkImporting} = storeToRefs(orderStore);
  const {bulkImportSnapshot, bulkCsvImportSnapshot} = orderStore;
  const {allShopsFilter, orderQueryParams, selectedFiltersAmount, datesFilter, periodFromFilter, periodUntilFilter} =
    storeToRefs(orderFiltersStore);

  const {setRecentlyAddedId, setRecentlyUpdatedId, recentlyAddedRowsById, recentlyUpdatedRowsById} =
    useTableRowHighlights('orders');

  const activePrinterGroupId = computed(() =>
    packingTableIsActivated.value ? uiSettings.value?.preferred_printer_group_id : undefined,
  );

  const removeFromOrderList = (orderId: Order['id']): void => {
    orderList.value = orderList.value.filter((order) => order.id !== orderId);
  };

  const orderShouldBeVisible = (changedOrder: Order): boolean => {
    const isDiscoveredTab = orderList.value.some((order) => order.status === OrderStatus.DISCOVERED);
    if (isDiscoveredTab) {
      if (changedOrder.status !== OrderStatus.DISCOVERED) {
        removeFromOrderList(changedOrder.id);
        return false;
      }
      return true;
    }

    const {filter} = orderQueryParams.value;
    if (!filter) {
      return true;
    }

    // An empty array (or absent value) is not an active filter, so it must not cause orders to be hidden.
    const isActiveFilter = (value: unknown): boolean => (Array.isArray(value) ? value.length > 0 : value != null);

    // An active filter on a supported field (status, orderId, shopId) that excludes the order hides it.
    const isExcludedBySupportedFilter =
      (isActiveFilter(filter.status) && !filter.status!.includes(changedOrder.status)) ||
      (isActiveFilter(filter.orderId) && !filter.orderId!.includes(changedOrder.id)) ||
      (isActiveFilter(filter.shopId) && !filter.shopId!.includes(changedOrder.shopId));

    if (isExcludedBySupportedFilter || changedOrder.status === OrderStatus.DISCOVERED) {
      removeFromOrderList(changedOrder.id);
      return false;
    }

    // An active filter on any other field can't be evaluated here, so we don't add the order;
    // the user needs to reload the tab/page to see it.
    const allowedFilterKeys = ['status', 'orderId', 'shopId'];
    const hasUnsupportedFilter = Object.entries(filter).some(
      ([key, value]) => isActiveFilter(value) && !allowedFilterKeys.includes(key),
    );

    return !hasUnsupportedFilter;
  };

  const addOrder = (order: Order) => {
    if (!orderShouldBeVisible(order)) {
      return;
    }

    orderList.value.push(order);
    setRecentlyAddedId(order.id);
  };

  const addOrderToTopOfTheList = (order: Order) => {
    if (!orderShouldBeVisible(order)) {
      return;
    }

    orderList.value.unshift(order);
    setRecentlyAddedId(order.id);
  };

  const upsertOrder = (changedOrder: Order) => {
    if (!orderShouldBeVisible(changedOrder)) {
      return;
    }

    const index = orderList.value.findIndex((order) => order.id === changedOrder.id);

    if (index === -1) {
      addOrderToTopOfTheList(changedOrder);
    } else {
      orderList.value[index] = changedOrder;
      setRecentlyUpdatedId(changedOrder.id);
    }
  };

  const getOrders = async (query?: NonNullable<GetOrdersData['query']>) => {
    orderAbortController?.abort();

    orderAbortController = new AbortController();
    const client = useOrderService();
    const {orderQueryParams} = orderFiltersStore;
    const params: GetOrdersData['query'] = {
      ...orderQueryParams,
      // Request per-status counts for every tab so they can all be displayed at once, independent
      // of the active status filter. The backend scopes these counts to all filters except status.
      aggregations: [...activeOrderStatuses],
    };

    if (query?.pageToken) {
      params.pageToken = query.pageToken;
    }

    if (query?.sort && Object.keys(query?.sort).length > 0) {
      params.sort = query.sort;
    }

    const sanitizedQueryParams = removeEmpty<GetOrdersData['query']>(params);

    const response = await client.getOrders(sanitizedQueryParams, orderAbortController);

    const existingIds = new Set(orderList.value.map((o: Order) => o.id));
    const newUniqueOrders = response.results.filter((o: Order) => !existingIds.has(o.id));
    orderList.value.push(...newUniqueOrders);

    return response;
  };

  const getOrderById = async (orderId: string): Promise<Order | undefined> => {
    orderByIdAbortController?.abort();
    const orderStore = useOrderStore();
    const client = useOrderService();

    const orderFromStore = orderStore.getOrderById(orderId);

    if (orderFromStore) {
      return orderFromStore;
    }

    orderByIdAbortController = new AbortController();

    const response = await client.getOrders(
      {
        filter: {orderId: [orderId]},
      },
      orderByIdAbortController,
    );

    return response.results[0];
  };

  const reloadOrders = async (orderIds: string[]) => {
    const client = useOrderService();
    orderByIdAbortController = new AbortController();

    const response = await client.getOrders(
      {
        filter: {orderId: orderIds},
      },
      orderByIdAbortController,
    );

    response.results.forEach((order) => {
      upsertOrder(order);
    });
  };

  const removeOrder = (orderId: string) => {
    if (!orderStore.getOrderById(orderId)) {
      return;
    }

    orderStore.removeOrder(orderId);
  };

  const addOrderNote = async (data: NonNullable<PostAddNoteData['body']>[number]) => {
    const {postAddOrderNote} = useOrderService();
    const {getUserId} = useAcl();
    const response = await postAddOrderNote([data]);

    if (response.length < 1 || response[0].status !== 200) {
      throw new Error('Failed to add order note');
    }

    const userId = getUserId();

    orderStore.addOrderNote({
      orderId: data.id,
      newOrderNote: {
        ...data,
        // Optimistic UI - save the note with temporary props
        id: TemporaryOrderNoteIdPrefix + response[0].ids[0],
        notedAt: new Date().toISOString(),
        type: 'PRINCIPAL',
        principal: `user/${userId}`,
      },
    });
  };

  const editOrderNote = async (data: NonNullable<PostEditNoteData['body'][number]>) => {
    const {postEditOrderNote} = useOrderService();
    const response = await postEditOrderNote([data]);

    if (response.length < 1 || response[0].status !== 200) {
      throw new Error('Failed to edit order note');
    }

    orderStore.updateOrderNote({
      orderId: data.id,
      noteId: data.noteId,
      text: data.text,
      // Optimistic UI - expect the current date
      updatedAt: new Date(),
    });
  };

  const removeOrderNotes = async (data: NonNullable<PostRemoveNotesData['body'][number]>) => {
    const {postRemoveOrderNotes} = useOrderService();

    const response = await postRemoveOrderNotes([data]);

    if (response.length < 1 || response[0].status !== 200) {
      throw new Error('Failed to remove order notes');
    }

    orderStore.removeOrderNotes({
      orderId: data.id,
      noteIds: data.noteIds,
    });
  };

  const assignOrdersToUser = async (data: NonNullable<PostAssignToUserData['body']>) => {
    const client = useOrderService();
    const responses = await client.postAssignOrdersToUser(data);

    const {succeeded: assignmentsSucceeded, failed: assignmentsFailed} = groupBulkResponsesBySuccess(data, responses);

    assignmentsSucceeded.forEach((request) => {
      orderStore.assignedUserToOrder({
        orderId: request.id,
        userId: request.userId,
      });
    });

    return {
      assignmentsSucceeded,
      assignmentsFailed,
    };
  };

  const unassignOrdersFromUser = async (data: NonNullable<PostUnassignFromUserData['body']>) => {
    const client = useOrderService();
    const responses = await client.postUnassignOrdersFromUser(data);

    const {succeeded: unassignmentsSucceeded, failed: unassignmentsFailed} = groupBulkResponsesBySuccess(
      data,
      responses,
    );

    unassignmentsSucceeded.forEach(({id}) => {
      orderStore.removeAssignedUserFromOrder(id);
    });

    return {
      unassignmentsSucceeded,
      unassignmentsFailed,
    };
  };

  const resetOrderList = () => {
    orderList.value = [];
  };

  const getAllOrdersById = (orderIds: string[]) => {
    const orderIdList = new Set(orderIds);

    return orderList.value.filter((order) => orderIdList.has(order.id));
  };

  const reassignOrdersToUser = async ({orderIds, userId}: {orderIds: string[]; userId: string}) => {
    const {unassignmentsSucceeded, unassignmentsFailed} = await unassignOrdersFromUser(orderIds.map((id) => ({id})));

    const {assignmentsFailed, assignmentsSucceeded} = await assignOrdersToUser(
      unassignmentsSucceeded.map((request) => {
        return {id: request.id, userId};
      }),
    );

    const failedOrderReassignments = new Map<string, ProblemDetailsClient | ProblemDetailsInternalServer>();

    unassignmentsFailed.forEach((data) => {
      failedOrderReassignments.set(data.request.id, data.response);
    });
    assignmentsFailed.forEach((data) => {
      failedOrderReassignments.set(data.request.id, data.response);
    });

    const succeededOrderReassignments = [...assignmentsSucceeded];

    /**
     * The API returns succeeded and failed order assignments in the same array.
     * We return the failed objects so we can handle them further in the UI (e.g. a warning notification for the user)
     */
    return {failedOrderReassignments, succeededOrderReassignments};
  };

  const createFromShippablePackage = (data: PostCreateFromShippablePackagesData['body']) => {
    const client = useOrderService();
    return client.postCreateFromShippablePackages(data);
  };

  const addPackages = (data: PostAddPackagesData['body']) => {
    const client = useOrderService();
    return client.postAddPackages(data);
  };

  const preparePackagesForShipment = async (data: PostPreparePackagesForShipmentData['body']) => {
    const client = useOrderService();
    const response = await client.postPreparePackagesForShipment(data);

    return response;
  };

  const unpreparePackagesForShipment = async (data: PostUnpreparePackagesForShipmentData['body']) => {
    const client = useOrderService();
    const response = await client.postUnpreparePackagesForShipment(data);

    const {succeeded, failed} = groupBulkResponsesBySuccess(data, response);

    const orderIdsToReload = succeeded.map((request) => request.id);
    if (orderIdsToReload.length > 0) {
      await reloadOrders(orderIdsToReload);
    }

    return {succeeded, failed};
  };

  const cancelOrder = async (data: PostCancelData['body']) => {
    const client = useOrderService();
    const response = await client.postCancelOrders(data);
    const {succeeded, failed} = groupBulkResponsesBySuccess(data, response);

    succeeded.forEach(({id}) => {
      orderStore.updateOrderStatus(id, OrderStatus.CANCELED);
    });

    return {succeeded, failed};
  };

  const importOrders = (data: PostImportData['body']) => {
    const client = useOrderService();
    return client.postImportOrders(data);
  };

  const getMulticolloIds = (loadedOrder: Order, currentPackageId: string): string[] => {
    const packages = loadedOrder.packages ?? [];

    if (!packages.length) {
      return [];
    }

    const multicolloGroupId = packages.find((packageItem) => packageItem.id === currentPackageId)?.shipment?.multicollo
      ?.id;

    if (!multicolloGroupId) {
      return [];
    }

    return packages.flatMap((packageItem) =>
      packageItem.shipment?.multicollo?.id === multicolloGroupId ? [packageItem.id] : [],
    );
  };

  const handleOrderImportCompleted = (order: Order) => {
    const orderIsImportedViaBulk = Boolean(bulkImportSnapshot.getItemById(order.id));

    if (!orderIsImportedViaBulk) {
      addNotification({
        variant: 'success',
        body: 'orders.notifications.import.success',
      });

      return;
    }

    bulkImportSnapshot.finishItem({id: order.id, success: true, after: order});
    setRecentlyUpdatedId(order.id);
  };

  const handleOrderCsvImportCompleted = (order: Order) => {
    const snapshotItemId =
      order.externalReferences?.source === 'IMPORT' ? order.externalReferences.sourceId : undefined;
    const item = snapshotItemId ? bulkCsvImportSnapshot.getItemById(snapshotItemId) : undefined;

    if (!item) {
      return;
    }

    bulkCsvImportSnapshot.updateItem({...item, after: order, orderCreated: true});

    // Orders that contain shipments have additional api calls which are called later, so they stay
    // pending until those report back.
    if (item.hasShipment) {
      return;
    }

    bulkCsvImportSnapshot.finishItem({id: item.id, success: true, after: order});
    setRecentlyUpdatedId(order.id);
  };

  const downloadPicklist = async (payload: GetPicklistData['query']) => {
    const client = useOrderService();

    const response = await client.getPicklist(payload, {
      printerGroupId: activePrinterGroupId.value,
    });

    if (activePrinterGroupId.value) {
      pendingPrintJobs.value.push(...payload.orderId);
      return;
    }

    await file.printOrDownload(response, {
      shouldPrint: isPrintMode.value,
      filename: `myparcel-picklist-${dayjs().format('YYYYMMDDHHmmss')}.pdf`,
    });
  };

  const downloadPackingSlips = async (payload: Omit<GetPackingSlipsData['query'], 'shipmentLabelsLayout'>) => {
    const client = useOrderService();

    const shipmentLabelsLayout = shipmentLabelLayoutMap[accountSettings.value.order_settings?.shipment_label];
    const response = await client.getPackingSlips(
      {...payload, shipmentLabelsLayout},
      {
        printerGroupId: activePrinterGroupId.value,
      },
    );

    if (activePrinterGroupId.value) {
      pendingPrintJobs.value.push(...payload.orderId);
      return;
    }

    await file.printOrDownload(response, {
      shouldPrint: isPrintMode.value,
      filename: `myparcel-packing-slip-${dayjs().format('YYYYMMDDHHmmss')}.pdf`,
    });
  };

  const getDownloadablePackages = async (order: Order): Promise<PackageResponse[]> => {
    if (!order.packages) return [];

    const results = await Promise.all(
      order.packages.map(async (pkg) => ({
        pkg,
        isDownloadable: pkg.shipment ? await isShipmentDownloadable(pkg.shipment) : false,
      })),
    );
    return results.filter(({isDownloadable}) => isDownloadable).map(({pkg}) => pkg);
  };

  const shouldShowOrderNotes = computed(() => {
    return (
      hasPermission('fulfilmentWriteOrderNotes') &&
      (hasSubscriptionFeature(SubscriptionFeature.AllowOrderNotes).value || Boolean(activeBundlesSubscription.value))
    );
  });

  onUnmounted(() => {
    orderAbortController?.abort();
    orderByIdAbortController?.abort();
  });

  return {
    addOrder,
    addOrderNote,
    addOrderToTopOfTheList,
    addPackages,
    allShopsFilter,
    assignOrdersToUser,
    createFromShippablePackage,
    datesFilter,
    periodFromFilter,
    periodUntilFilter,
    bulkImportSnapshot,
    bulkCsvImportSnapshot,
    isBulkImporting,
    editOrderNote,
    cancelOrder,
    getAllOrdersById,
    getDownloadablePackages,
    getOrderById,
    removeOrder,
    getMulticolloIds,
    getOrders,
    downloadPicklist,
    downloadPackingSlips,
    handleOrderImportCompleted,
    handleOrderCsvImportCompleted,
    importOrders,
    orderList,
    orderListAmount,
    orderQueryParams,
    reassignOrdersToUser,
    recentlyAddedOrderIds: recentlyAddedRowsById,
    recentlyUpdatedOrderIds: recentlyUpdatedRowsById,
    removeOrderNotes,
    resetOrderList,
    selectedFiltersAmount,
    preparePackagesForShipment,
    reloadOrders,
    unpreparePackagesForShipment,
    unassignOrdersFromUser,
    upsertOrder,
    updateOrderStatus: orderStore.updateOrderStatus,
    statusesWithNewOrders,
    flagIncomingOrder: orderStore.flagIncomingOrder,
    clearNewOrderStatus: orderStore.clearNewOrderStatus,
    resetNewOrderStatuses: orderStore.resetNewOrderStatuses,
    shouldShowOrderNotes,
    getOrderByIdFromStore: orderStore.getOrderById,
    clearOrderFilters: orderFiltersStore.clearOrderFilters,
    initOrderQueryParams: orderFiltersStore.initOrderQueryParams,
    setAllShopsFilter: orderFiltersStore.setAllShopsFilter,
    setShopIdsFilter: orderFiltersStore.setShopIdsFilter,
    updateAssignUserIdFilter: orderFiltersStore.updateAssignUserIdFilter,
    setDatesFilter: orderFiltersStore.setDatesFilter,
    updateOrderQueryFilter: orderFiltersStore.updateOrderQueryFilter,
  };
};
